import { FC, useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography, Grid, Dialog, DialogContent } from "@mui/material";
import BugReportIcon from "@mui/icons-material/BugReport";
import {
  durationMinutesToTime,
  formatDate,
  timeToDurationMinutes,
  toHoursAndMinutes,
} from "common/utils";
import intl from "components/i18n/ReactIntlWrapper";
import JiraIssueSearch from "components/Project/JiraIssue/JiraIssueSearch";
import {
  CreateTimesheetCommand,
  JiraIssue,
  Timesheet,
  UpdateTimesheetCommand,
} from "axiosApi/models";
import Spinner from "../Spinner";
import ErrorSummary from "components/common/ErrorSummary";
import ImaskInput from "./ImaskInput";
import { Delete, Edit, HighlightOff, Save } from "@mui/icons-material";
import { DialogActions } from "@mui/material";
import { useApi } from "api/ApiProvider";
import Api from "axiosApi/api";
import { getUserEntity } from "common/UserEntityProvider";
import { useDialog } from "common/DialogProvider";
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider";

type TimesheetEditModal = {
  timesheet: Timesheet | null,
  onComplete?: (timesheet: Timesheet) => void,
  disabled?: boolean;
}

const TimesheetEditModal: FC<TimesheetEditModal> = ({ timesheet, onComplete, disabled }) => {

  const [loading, setLoading] = useState(false);
  const [jiraSearch, setJiraSearch] = useState(false);
  const [open, setOpen] = useState(false);
  const [duration, setDuration] = useState<string>();
  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [jiraEnabled, setJiraEnabled] = useState(false);

  const userDetails = getUserEntity();
  const idEmployee = userDetails?.entityId;
  
  const ref = useRef<HTMLDivElement>(null);
  const { timesheetApi } : Api = useApi();
  const toast = useToastMessageQueue();
  const { openDialog } = useDialog();

  const handleIssuesAdd = (issues: JiraIssue[]) => {
    const jiraIssueDescription = issues.map((issue) => {
      return `${issue.key} ${issue.summary}`;
    }).join("\n");

    setDescription((prevDescription) => {
      return prevDescription ? `${prevDescription}\n${jiraIssueDescription}` : jiraIssueDescription;
    });
  }

  const handleInputChange = (e : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setDescription(e.target.value);
  };

  const closeModal = () => {
    setOpen(false);

  };
  
  const handleDelete = async (id) => {
    setLoading(true);
    const response = await timesheetApi.apiVversionTimesheetIdDelete(id, "1", {});
    if (response.status == 200) {
      toast.success({ message: intl.get('timesheetComponent.toast.deleteconfirmation') });
      onComplete?.(timesheet);
      closeModal();
    } else {
      toast.error({ message: intl.get('timesheetComponent.toast.deleteError') });
    }
    setLoading(false);
  }

  const handleDeleteDay = (id) => {
    openDialog({
      title: intl.get("delete.modal.title"),
      message: intl.get("timesheetComponent.delete.record"),
      type: 'confirm',
      confirmButton: {
          confirmLabel: 'Accept',
          handleConfirm: (message) => {
            message.handleClose(message);
            handleDelete(id);
          }
        },
        cancelButton: {
          cancelLabel: intl.get("delete.modal.cancel.button"),
          handleCancel: (message) => {
            message.handleClose(message);
          }
        }
    });
  };

  useEffect(() => {
    var project = timesheet?.task?.job?.project;
    if (project) {
      setJiraEnabled(
        project.jiraUrl != null &&
          project.jiraProjectKey != null &&
          project.jiraUser != null &&
          project.jiraToken != null
      );
    }
    if (timesheet) {
      setDescription(timesheet.description || "");
      setDuration(durationMinutesToTime(timesheet.duration));
    }
  }, [timesheet]);

  useEffect(() => {
    if(open) {
      setTimeout(() => {
        document.getElementById("duration")?.focus();
      }, 0);
    }
  }, [open, ref.current]);

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};
    const durationInMinutes = timeToDurationMinutes(duration || "00:00");
    if (!durationInMinutes || durationInMinutes <= 0) { 
      errors.duration = intl.get("timesheetForm.error.durationRequired");
    } else if (durationInMinutes > 1440) {
      errors.duration = intl.get("timesheetForm.error.durationInvalid");
    }
    if (!timesheet?.task?.id) {
      errors.idTask = intl.get("timesheetForm.error.taskRequired");
    }
    if (!timesheet?.task?.job?.idProject) {
      errors.idProject = intl.get("timesheetForm.error.projectRequired");
    }
    if (!timesheet?.date) {
      errors.date = intl.get("timesheetForm.error.dateRequired");
    }
    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }
    if(!timesheet?.id || timesheet.id === -1) {
      const cmd: CreateTimesheetCommand = {
        idEmployee: idEmployee,
        idTask: timesheet?.task?.id,
        description: description,
        date: timesheet?.date,
        duration: timeToDurationMinutes(duration || "00:00"),
        startTime: timesheet?.startTime,
      };
      setLoading(true);
      timesheetApi.apiVversionTimesheetPost("1", cmd, {})
        .then((res) => {
          setLoading(false);
          closeModal();
          onComplete?.(res?.data?.data);
        })
        .catch((error) => {
          setLoading(false);
          setErrors({ apiError: error.message });
        });
    }else{
      const cmd: UpdateTimesheetCommand = {
        id: timesheet.id,
        idEmployee: timesheet.idEmployee,
        idTask: timesheet.task.id,
        description: description,
        date: timesheet.date,
        duration: timeToDurationMinutes(duration || "00:00"),
        startTime: timesheet.startTime,
      };
      setLoading(true);
      timesheetApi.apiVversionTimesheetPut("1", cmd, {})
        .then((res) => {
          setLoading(false);
          closeModal();
          onComplete?.(res?.data?.data);
        })
        .catch((error) => {
          setLoading(false);
          setErrors({ apiError: error.message });
        });
    }
  }

  // Check if project and task are selected
  const canOpenModal = timesheet?.task?.id && timesheet?.task?.job?.project?.id;

  const handleTextFieldClick = () => {
    if (canOpenModal) {
      setOpen(true);
    }
  };

  return (<>
    <TextField
      disabled={disabled || !canOpenModal}
      sx={{
        cursor: canOpenModal ? "pointer!important" : "default",
      }}
      slotProps={{
        input: {
          endAdornment: canOpenModal ? <Edit sx={{width: 18, height: 18}} /> : null,
          placeholder: "hh:mm",
          readOnly: true,
          sx: { cursor: canOpenModal ? "pointer!important" : "default" },
        },
        htmlInput: {
          sx: { cursor: canOpenModal ? "pointer!important" : "default" },
        },
      }}
      value={duration || timesheet?.duration && toHoursAndMinutes(timesheet.duration)}
      onClick={handleTextFieldClick}
    />
      <Dialog open={open} onClose={closeModal} maxWidth="md" fullWidth>
        <Box component={"form"} noValidate autoComplete="off" onSubmit={onSubmit}>
          <DialogContent>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <ErrorSummary errors={errors} showEverything />
                </Grid>
                <Grid size={{ xs: 12, md: 7}}>
                  <Typography variant="subtitle2">
                    {intl.get("timesheetForm.clientproject")}
                  </Typography>
                  {errors.idProject && (
                    <Typography color="error">{errors.idProject}</Typography>
                  )}
                  <Typography color="text.secondary">
                    {timesheet?.task?.job?.project?.name}
                  </Typography>
                  <Box mt={2}>
                    <Typography variant="subtitle2" className="mt-4">
                      {intl.get("timesheetForm.task")}
                    </Typography>
                    {errors.idTask && (
                      <Typography color="error">{errors.idTask}</Typography>
                    )}
                    <Typography color="text.secondary">
                      {timesheet?.task?.job?.description}
                    </Typography>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="subtitle2">
                    {intl.get("timesheetForm.date")}
                  </Typography>
                  {errors.date && <Typography color="error">{errors.date}</Typography>}
                  <Typography color="text.secondary">
                    {timesheet?.date && formatDate(timesheet.date)}
                  </Typography>

                  <Box mt={2}>
                    <Typography variant="subtitle2">
                      {intl.get("timesheetForm.duration")}
                    </Typography>
                    <ImaskInput
                      id="duration"
                      inputRef={ref}
                      mask="00:00"
                      cursorStart={1}
                      overwrite={true}
                      defaultValue={duration}
                      value={duration}
                      onAccept={(value) => {
                        setDuration(value);
                      }}
                    />
                    {errors.duration && (
                      <Typography color="error">{errors.duration}</Typography>
                    )}
                  </Box>
                </Grid>

                <Grid size={12}>
                  <Typography variant="subtitle2">
                    {intl.get("timesheetForm.description")}
                  </Typography>
                  {jiraEnabled && (
                    <BugReportIcon
                      onClick={() => {
                        setJiraSearch(!jiraSearch);
                      }}
                      className="mb-1"
                      role="button"
                    />
                  )}
                  {jiraEnabled && jiraSearch && (
                    <JiraIssueSearch
                      idProject={timesheet.task.job.idProject}
                      handleIssuesAdd={handleIssuesAdd}
                      tasks={[]}
                    />
                  )}
                  <TextField
                    fullWidth
                    id="Description"
                    name="description"
                    placeholder={intl.get("timesheetForm.placeholder.description")}
                    multiline
                    rows={5}
                    value={description}
                    onChange={handleInputChange}
                    margin="normal"
                  />
                </Grid>
              </Grid>
          </DialogContent>
          <DialogActions>
            {loading ? (
              <Spinner small />
            ) : (
              <>
                <Button variant="outlined" color="inherit" startIcon={<HighlightOff/>} onClick={closeModal}>
                  {intl.get("cancel.button")}
                </Button>
                {timesheet?.id && (
                  <Button
                  variant="outlined"
                  color="error"
                  onClick={() => handleDeleteDay(timesheet?.id)}
                  startIcon={<Delete />}
                  >
                    {intl.get("delete.modal.delete.button")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  startIcon={<Save />}
                  >
                  {intl.get("save.button")}
                </Button>
              </>
            )}
          </DialogActions>
        </Box>
      </Dialog>
  </>);
};

export default TimesheetEditModal;
